/**
 * BlackVue Fleeta Device List API Functions
 * Handles device management and listing functionality
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../auth/authUtils';
import { getApiGateway } from '../api/endpointUtils';
import type {
  DeviceListParams,
  DeviceListResponse,
  DeviceListItem,
  DeviceInfo,
  DeviceGroupData,
  DeviceListStatistics,
  DeviceStatusParams,
  DeviceStatusResponse,
  DeviceRegistrationInfo
} from './types';

import { DEVICE_LIST_ERROR_MESSAGES, DEVICE_STATUS_ERROR_MESSAGES } from './types';
import { processDeviceListData } from './grouping';

/**
 * Get device list from BlackVue Fleeta Cloud
 * Retrieves list of registered devices and groups
 * 
 * @param params - Request parameters (optional - uses auth config if not provided)
 * @returns Promise with device list response
 */
export async function getDeviceList(params?: Partial<DeviceListParams>): Promise<DeviceListResponse> {
  // AuthStore에서 실시간으로 auth 정보 가져오기
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: DeviceListParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    console.log('📱 [Device API] Requesting device list:', {
      email: requestParams.email,
      hasUserToken: !!requestParams.user_token,
      tokenType: requestParams.tokenType,
      keyword: requestParams.keyword,
      startIndex: requestParams.startIndex,
      endIndex: requestParams.endIndex
    });

    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('email', requestParams.email);
    queryParams.append('user_token', requestParams.user_token);
    queryParams.append('tokenType', requestParams.tokenType || 'web');
    
    if (requestParams.keyword) {
      queryParams.append('keyword', requestParams.keyword);
    }
    if (requestParams.startIndex && requestParams.startIndex >= 1) {
      queryParams.append('startIndex', requestParams.startIndex.toString());
    }
    if (requestParams.endIndex && requestParams.endIndex >= 1) {
      queryParams.append('endIndex', requestParams.endIndex.toString());
    }

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/DeviceList?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: DeviceListResponse = await response.json();

    console.log('📡 [Device API] Raw API response:', {
      resultcode: data.resultcode,
      message: data.message,
      hasResponse: !!(data.response || data.deviceListInfo),
      deviceCount: data.response?.deviceCount || data.deviceCount,
      deviceListInfo: data.response?.deviceListInfo?.length || data.deviceListInfo?.length || 0,
      rawResponse: data.response || data
    });


    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = DEVICE_LIST_ERROR_MESSAGES[data.resultcode as keyof typeof DEVICE_LIST_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    // Extract data from response object
    const responseData = data.response || {
      deviceCount: data.deviceCount || 0,
      deviceLimitCount: data.deviceLimitCount || 0,
      sharedEventMap: data.sharedEventMap || 'off',
      deviceListInfo: data.deviceListInfo || []
    };
    
    if (!responseData) {
      console.warn('⚠️ [Device API] API returned null/undefined response object');
      return {
        resultcode: data.resultcode,
        message: data.message,
        deviceCount: 0,
        deviceLimitCount: 0,
        sharedEventMap: 'off',
        deviceListInfo: [],
        groupedData: {
          groups: [],
          statistics: {
            total: 0, online: 0, offline: 0, grouped: 0, ungrouped: 0,
            videoSharing: 0, normalMode: 0, parkingMode: 0, 
            wifiOnly: 0, cloudEnabled: 0, withBattery: 0
          }
        }
      };
    }
    
    // Ensure deviceListInfo exists and is an array
    if (!responseData.deviceListInfo) {
      console.warn('⚠️ [Device API] API returned null/undefined deviceListInfo in response, setting to empty array');
      responseData.deviceListInfo = [];
    }
    
    // Process grouping and statistics in API layer
    const { groups, statistics } = processDeviceListData(responseData.deviceListInfo);
    
    console.log('📱 [Device API] Final processed response:', {
      devices: responseData.deviceListInfo.length,
      groups: groups.length,
      statistics: statistics.total,
      resultcode: data.resultcode,
      deviceCount: responseData.deviceCount
    });

    // Return in expected format
    return {
      resultcode: data.resultcode,
      message: data.message,
      deviceCount: responseData.deviceCount || 0,
      deviceLimitCount: responseData.deviceLimitCount || 0,
      sharedEventMap: responseData.sharedEventMap || 'off',
      deviceListInfo: responseData.deviceListInfo || [],
      groupedData: {
        groups,
        statistics
      }
    };

  } catch (error) {
    console.error('❌ [Device API] Device list request failed:', error);
    throw error;
  }
}

/**
 * Check device registration status
 * Checks if device is already registered before cloud registration
 * 
 * @param params - Device status check parameters
 * @returns Promise with device registration status
 */
export async function getDeviceStatus(params: DeviceStatusParams): Promise<DeviceStatusResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Build query string
    const queryParams = new URLSearchParams();
    queryParams.append('psn', params.psn);

    // Make API request
    const response = await fetch(`${API_GATEWAY}/DashCam/DeviceList/status?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: DeviceStatusResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = DEVICE_STATUS_ERROR_MESSAGES[data.resultcode as keyof typeof DEVICE_STATUS_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Check if device is available for cloud registration
 * Convenience function that interprets device status response
 * 
 * @param psn - Device PSN to check
 * @returns Promise with boolean indicating if device is available for registration
 */
export async function isDeviceAvailableForRegistration(psn: string): Promise<{
  available: boolean;
  reason?: string;
  deviceInfo?: DeviceRegistrationInfo;
}> {
  try {
    const statusResponse = await getDeviceStatus({ psn });
    
    // Empty response means device is available for cloud registration
    if (!statusResponse.response || Object.keys(statusResponse.response).length === 0) {
      return {
        available: true,
        reason: 'Device is available for cloud registration'
      };
    }
    
    // Non-empty response means device is already registered
    const deviceInfo = statusResponse.response as DeviceRegistrationInfo;
    return {
      available: false,
      reason: `Device is already registered to ${deviceInfo.email}`,
      deviceInfo
    };
    
  } catch (error) {
    throw error;
  }
}


/**
 * Check if device API is ready (authentication configured)
 * @returns True if authentication is configured
 */
export function isDeviceApiReady(): boolean {
  return getAuthConfigFromStore() !== null;
}